import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { commodityCodesQuery } from "@/lib/reference";

export const Route = createFileRoute("/_authenticated/commodity-codes")({
  component: CommodityCodesPage,
});

function CommodityCodesPage() {
  const [draft, setDraft] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const result = useQuery(commodityCodesQuery(q, page));
  const rows = result.data?.results ?? [];
  const count = result.data?.count ?? 0;
  const lastPage = Math.max(1, Math.ceil(count / 50));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Commodity codes</h1>
        <p className="text-muted-foreground text-sm">
          {count} codes. Search by code prefix (e.g. 8708) or Dutch description or tag.
        </p>
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQ(draft.trim());
          setPage(1);
        }}
      >
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="8413.81 or waterpomp"
          className="max-w-sm"
        />
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Tariff</TableHead>
              <TableHead>Permit</TableHead>
              <TableHead>Declares as</TableHead>
              <TableHead>Tags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((code) => (
              <TableRow key={code.id}>
                <TableCell className="font-mono">{code.code}</TableCell>
                <TableCell className="max-w-md">
                  <div>{code.description}</div>
                  {code.path && code.path !== code.description && (
                    <div className="text-muted-foreground truncate text-xs" title={code.path}>
                      {code.path}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">{Number(code.tariff_percent)}%</TableCell>
                <TableCell>
                  {code.stempel ? <Badge variant="destructive">{code.stempel}</Badge> : "—"}
                </TableCell>
                <TableCell className="font-mono">{code.defaults_to_code ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground max-w-xs truncate text-xs">
                  {code.tags.slice(0, 6).join(", ")}
                  {code.tags.length > 6 && ` +${code.tags.length - 6}`}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                  {result.isLoading ? "Loading…" : "No codes match."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {lastPage > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span>
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
